import React, { useState, useRef, useEffect } from 'react';
import { Box, Flex, Icon } from '@chakra-ui/react';
import { Lock01 } from '@kula-ai/icons/dual-tone/Lock01';
import { KText } from '@/ui-library/components';

import type { MinimumNoticePopoverProps } from './MinimumNoticePopover.interface';

export const MinimumNoticePopover: React.FC<MinimumNoticePopoverProps> = ({
  minimumNoticeConfig,
  children,
  placement = 'auto',
  offset = 2,
  followCursor = true,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const formatMinimumNoticeTime = (): string => {
    if (
      minimumNoticeConfig &&
      minimumNoticeConfig.value &&
      minimumNoticeConfig.type
    ) {
      const { value, type } = minimumNoticeConfig;
      if (type === 'days') {
        return `${value} day${value === 1 ? '' : 's'}`;
      } else {
        return `${value} hour${value === 1 ? '' : 's'}`;
      }
    }

    return '0 hours';
  };

  const initiateMouseMove = (event: MouseEvent) => {
    const offsetYPadding = 10;
    const containerWidth = document
      .getElementById('schedule-content-container')
      .getBoundingClientRect().width;

    const leftPad = window.innerWidth - containerWidth;

    let top = event.clientY + offsetYPadding;
    let left = event.clientX - leftPad;

    setPosition({ top, left });
    setIsVisible(true);
  };

  const initiateMouseLeave = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const addListeners = () => {
      const minimumNoticeElements = container.querySelectorAll(
        '.bg-minimum-notice-disabled-slots'
      );
      minimumNoticeElements.forEach((element) => {
        element.addEventListener('mousemove', initiateMouseMove);
        element.addEventListener('mouseleave', initiateMouseLeave);
      });

      return () => {
        minimumNoticeElements.forEach((element) => {
          element.removeEventListener('mouseenter', initiateMouseMove);
          element.removeEventListener('mouseleave', initiateMouseLeave);
        });
      };
    };

    const cleanup = addListeners();

    const observer = new MutationObserver(() => {
      cleanup();
      addListeners();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    return () => {
      cleanup();
      observer.disconnect();
    };
  }, [minimumNoticeConfig, followCursor, placement, offset, isVisible]);

  return (
    <>
      <Box ref={containerRef} position="relative" width="100%" height="100%">
        {children}
      </Box>
      {isVisible && (
        <Box
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          bg="base-white"
          borderRadius="12px"
          boxShadow="0px 4px 16px rgba(0, 0, 0, 0.08), 0px 2px 4px rgba(0, 0, 0, 0.04)"
          border="1px solid"
          borderColor="border-color"
          zIndex={999999}
          pointerEvents="none"
          width="280px"
          px="14px"
          py="14px"
        >
          <Flex gap="10px" alignItems="flex-start">
            <Box
              bg="pastel-4-light"
              borderRadius="6px"
              p="6px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              width="30px"
              height="30px"
            >
              <Icon
                as={Lock01}
                color="pastel-4-dark"
                width="18px"
                height="18px"
              />
            </Box>

            <Flex direction="column" gap="4px" flex="1" pt="1px">
              <KText color="secondary-900" size="sm-medium" lineHeight="20px">
                {formatMinimumNoticeTime()} minimum notice
              </KText>

              <KText color="secondary-600" lineHeight="16px" size="sm">
                Select slots beyond this timeframe to give candidates options
              </KText>
            </Flex>
          </Flex>
        </Box>
      )}
    </>
  );
};
